import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { useBillingSettings, useBillingActions } from "@/hooks/useBilling";
import { TextField, SelectField } from "@/components/kit/fields";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const PROVIDERS = [
  { value: "mock", label: "Mock (testing)" }, { value: "trongrid", label: "TronGrid" },
  { value: "tronscan", label: "Tronscan" }, { value: "custom", label: "Custom API" },
];
const NETWORKS = ["TRC20", "ERC20", "BEP20"];
const TTL_OPTS = [{ value: "30", label: "30 minutes" }, { value: "60", label: "60 minutes" }, { value: "120", label: "120 minutes" }];

export function PaymentProviders() {
  const { data: settings } = useBillingSettings();
  const { saveSettings } = useBillingActions();
  const [wallets, setWallets] = useState(null);
  const [ttl, setTtl] = useState(60);

  useEffect(() => { if (settings) { setWallets(JSON.parse(JSON.stringify(settings.wallets || []))); setTtl(settings.order_ttl_minutes || 60); } }, [settings]);
  if (!wallets) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const setW = (i, k, v) => setWallets((w) => { const n = [...w]; n[i] = { ...n[i], [k]: v }; return n; });
  const add = () => setWallets((w) => [...w, { id: `wallet-${Date.now()}`, network: "TRC20", currency: "USDT", address: "", label: "", active: true, provider_type: "mock", api_key: "", api_url: "", confirmations_required: 1 }]);
  const rm = (i) => setWallets((w) => w.filter((_, x) => x !== i));
  const save = async () => {
    await saveSettings.mutateAsync({ wallets: wallets.map((w) => ({ ...w, confirmations_required: Number(w.confirmations_required) || 1 })), order_ttl_minutes: Number(ttl) });
    toast.success("Payment providers saved");
  };

  return (
    <div className="space-y-5" data-testid="payment-providers">
      <p className="max-w-2xl text-sm text-muted-foreground">Manage the crypto wallets and verification providers used to collect SaaS subscription payments. API keys are visible only here and never exposed to tenants.</p>
      <div className="max-w-xs rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <SelectField label="Payment order lifetime" value={String(ttl)} onChange={(v) => setTtl(Number(v))} options={TTL_OPTS} testId="pp-order-ttl" />
      </div>
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-medium text-foreground">Wallets &amp; providers</h3>
          <Button onClick={add} variant="outline" data-testid="pp-add-wallet-btn" className="h-8 gap-1.5 border-white/15 text-xs"><Plus className="h-3.5 w-3.5" /> Add wallet</Button>
        </div>
        <div className="space-y-3">
          {wallets.map((w, i) => (
            <div key={w.id} className="space-y-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-4" data-testid={`pp-wallet-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><span className="text-sm font-medium text-foreground">{w.label || "Wallet"}</span><span className="rounded bg-white/[0.05] px-2 py-0.5 text-xs text-muted-foreground">{w.network} · {w.currency}</span></div>
                <div className="flex items-center gap-3"><div className="flex items-center gap-1.5 text-xs text-muted-foreground">Active <Switch checked={w.active !== false} onCheckedChange={(v) => setW(i, "active", v)} /></div><button onClick={() => rm(i)} className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button></div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <TextField label="Label" value={w.label} onChange={(v) => setW(i, "label", v)} testId={`pp-label-${i}`} />
                <SelectField label="Network" value={w.network} onChange={(v) => setW(i, "network", v)} options={NETWORKS} testId={`pp-network-${i}`} />
                <TextField label="Currency" value={w.currency} onChange={(v) => setW(i, "currency", v)} testId={`pp-currency-${i}`} />
                <SelectField label="Provider" value={w.provider_type || "mock"} onChange={(v) => setW(i, "provider_type", v)} options={PROVIDERS} testId={`pp-provider-${i}`} />
              </div>
              <TextField label="Wallet address" value={w.address} onChange={(v) => setW(i, "address", v)} testId={`pp-address-${i}`} />
              {w.provider_type && w.provider_type !== "mock" && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <TextField label="API key (private)" type="password" value={w.api_key} onChange={(v) => setW(i, "api_key", v)} testId={`pp-apikey-${i}`} />
                  {w.provider_type === "custom" && <TextField label="API base URL" value={w.api_url} onChange={(v) => setW(i, "api_url", v)} testId={`pp-apiurl-${i}`} />}
                  <TextField label="Confirmations required" type="number" value={w.confirmations_required ?? 1} onChange={(v) => setW(i, "confirmations_required", v)} testId={`pp-conf-${i}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={saveSettings.isPending} data-testid="pp-save" className="gap-2 bg-white text-black hover:bg-white/90"><Save className="h-4 w-4" /> Save providers</Button>
    </div>
  );
}
